import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Divider,
  Stack,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import SingleDropDown from "components/Dropdowns/SingleDropDown";
import TitleDefinition from "components/TitleDefinition";
import SnackBar from "components/SnackBar/SnackBar";
import { Currency, TitleDefinitionConstants } from "constants/constant";
import { useForm } from "react-hook-form";
import { CreateLocationProps } from "./data/CreateLocationProps";
import { LocationModel } from "object-models/location-model";


/**
 * Component for creating or updating design a location.
 */
export default function CreateLocationDesign(
  props: Readonly<CreateLocationProps>
) {
  const {
    handleSubmit,
    register,
    formState: { errors },
    clearErrors,
  } = useForm<LocationModel>();
  
  const [activeTab, setActiveTab] = useState(0);
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // Get current year for default values when creating new location
  const currentYear = new Date().getFullYear();
  const defaultYear = currentYear.toString();

  // Historical Emissions state
const [startYear, setStartYear] = useState<string>(
  props.location.startYear !== undefined && props.location.startYear !== null
    ? props.location.startYear.toString()
    : defaultYear
);
const [endYear, setEndYear] = useState<string>(
  props.location.endYear !== undefined && props.location.endYear !== null
    ? props.location.endYear.toString()
    : defaultYear
);
  const [baselineYear, setBaselineYear] = useState<string>(
    props.location.baseYear !== undefined && props.location.baseYear !== null
      ? props.location.baseYear.toString()
      : defaultYear
  );
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  // Local state to track input values for immediate UI updates
  const [localInputValues, setLocalInputValues] = useState<Record<string, string>>({});
  const [isLocalInitialized, setIsLocalInitialized] = useState(false);

  // Generate year options (2010-2050)
  const yearOptions = Array.from({ length: 41 }, (_, i) => {
    const year = (2010 + i).toString();
    return { text: year, value: year };
  });

  // Generate year columns based on start and end year
  const getYearColumns = () => {
    const start = parseInt(startYear);
    const end = parseInt(endYear);
    if (isNaN(start) || isNaN(end) || start > end) return [];
    
    const columns = [];
    for (let year = start; year <= end; year++) {
      columns.push(year.toString());
    }
    return columns;
  };

  const yearColumns = getYearColumns();

  // Generate baseline year options (from start to end year)
  const getBaselineYearOptions = () => {
    const start = parseInt(startYear);
    const end = parseInt(endYear);
    if (isNaN(start) || isNaN(end) || start > end) return [];
    
    const options = [];
    for (let year = start; year <= end; year++) {
      options.push({ text: year.toString(), value: year.toString() });
    }
    return options;
  };

  const baselineYearOptions = getBaselineYearOptions();

  // Initialize historical emission data when start or end year changes
  useEffect(() => {
    const start = parseInt(startYear);
    const end = parseInt(endYear);
    if (!isNaN(start) && !isNaN(end) && start <= end) {
      props.initializeHistoricalEmissionData(start, end);
    }
  }, [startYear, endYear]);

  // Debug: Log when props.location.historicalEmissionData changes
  useEffect(() => {
    
  }, [props.location.historicalEmissionDatas]);

  // Initialize years in the location model for new locations
  useEffect(() => {
    if (!props.isEdit) {
      const currentYearNum = new Date().getFullYear();
      props.fieldChangeHandler({
        target: { name: "startYear", value: currentYearNum }
      } as any);
      props.fieldChangeHandler({
        target: { name: "endYear", value: currentYearNum }
      } as any);
      props.fieldChangeHandler({
        target: { name: "baseYear", value: currentYearNum }
      } as any);
    }
  }, []);

  // Sync local state with props when baseYear changes
  useEffect(() => {
    if (props.location.baseYear) {
      setBaselineYear(props.location.baseYear.toString());
    }
  }, [props.location.baseYear]);

    useEffect(() => {
    if (props.location.startYear) {
      setStartYear(props.location.startYear.toString());
    }
  }, [props.location.startYear]);

  useEffect(() => {
    if (props.location.endYear) {
      setEndYear(props.location.endYear.toString());
    }
  }, [props.location.endYear]);

  // Initialize local input values from props.location.historicalEmissionData
  useEffect(() => {
    if (props.location.historicalEmissionDatas && props.location.historicalEmissionDatas.length > 0 && !isLocalInitialized) {
      const initialValues: Record<string, string> = {};
      props.location.historicalEmissionDatas.forEach((data) => {
        const year = data.emissionYear.toString();
        const fields = [
          'scope1GHGEmission', 'scope2GHGEmission',
          'purchasedGoodsAndServices', 'capitalGoods', 'fuelandEnergyRelatedActivities',
          'upstreamTransportationAndDistribution', 'wasteGeneratedinOperations', 'businessTravel',
          'employeeCommuting', 'upstreamLeasedAssets', 'downstreamTransportationAndDistribution',
          'processingofSoldProducts', 'useofSoldProducts', 'endofLifeTreatmentofSoldProducts',
          'downstreamLeasedAssets', 'franchises', 'investments', 'otherUpstream', 'otherDownstream'
        ];
        fields.forEach((field) => {
          const value = data[field as keyof typeof data];
          if (value !== null && value !== undefined && value !== 0 && (typeof value === 'number' || typeof value === 'string')) {
            initialValues[`${year}-${field}`] = typeof value === 'number' ? value.toString() : value;
          }
        });
      });
      if (Object.keys(initialValues).length > 0) {
        setLocalInputValues(prev => ({ ...prev, ...initialValues }));
      }
      setIsLocalInitialized(true);
    }
  }, [props.location.historicalEmissionDatas, isLocalInitialized]);

  // Helper function to get value for a specific year and field
  const getHistoricalValue = (year: string, fieldName: string): string => {
    const key = `${year}-${fieldName}`;
    
    // Use local state as the source of truth
    if (localInputValues[key] !== undefined) {
      return localInputValues[key];
    }
    
    // Fallback to props for initial render before local state is initialized
    const yearNum = parseInt(year);
    const data = props.location.historicalEmissionDatas?.find((d) => d.emissionYear === yearNum);
    if (!data) return "";
    const value = data[fieldName as keyof typeof data];
    if (value === null || value === undefined || value === 0) return "";
    if (typeof value === 'number') return value.toString();
    if (typeof value === 'string' && value !== "") return value;
    return "";
  };

  // Helper function to validate numeric input (only numbers and decimal point)
  const validateNumericInput = (value: string): string => {
    // Remove any characters that are not digits or decimal point
    let cleaned = value.replace(/[^0-9.]/g, '');
    
    // Ensure only one decimal point
    const parts = cleaned.split('.');
    if (parts.length > 2) {
      cleaned = parts[0] + '.' + parts.slice(1).join('');
    }
    
    return cleaned;
  };

  // Helper function to handle historical emission data change
  const handleHistoricalDataChange = (year: string, fieldName: string) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const key = `${year}-${fieldName}`;
    const rawValue = event.target.value;
    
    // Validate input - only allow numbers and decimal point
    const inputValue = validateNumericInput(rawValue);
    
    // Update local state immediately for responsive UI
    setLocalInputValues(prev => ({
      ...prev,
      [key]: inputValue
    }));
    
    // Update parent state
    const yearNum = parseInt(year);
    props.updateHistoricalEmissionData(yearNum, fieldName, inputValue);
  };

  // Helper function to handle blur - no longer clears local state
  const handleHistoricalDataBlur = (_year: string, _fieldName: string) => () => {
    // Keep local state - don't clear it on blur to prevent value disappearing
  };

  // Calculate total emissions for a specific year
  const calculateTotalEmissions = (year: string): number => {
    // Get values from local state first, then fallback to props
    const scope1Str = getHistoricalValue(year, "scope1GHGEmission");
    const scope2Str = getHistoricalValue(year, "scope2GHGEmission");
    const scope3Total = calculateScope3Total(year);

    const scope1 = parseFloat(scope1Str) || 0;
    const scope2 = parseFloat(scope2Str) || 0;
    
    return scope1 + scope2 + scope3Total;
  };

  // Calculate Scope 3 total for a specific year (sum of all categories)
  const calculateScope3Total = (year: string): number => {
    // Sum all scope 3 category values from local state
    const categoryFields = [
      'purchasedGoodsAndServices', 'capitalGoods', 'fuelandEnergyRelatedActivities',
      'upstreamTransportationAndDistribution', 'wasteGeneratedinOperations', 'businessTravel',
      'employeeCommuting', 'upstreamLeasedAssets', 'downstreamTransportationAndDistribution',
      'processingofSoldProducts', 'useofSoldProducts', 'endofLifeTreatmentofSoldProducts',
      'downstreamLeasedAssets', 'franchises', 'investments', 'otherUpstream', 'otherDownstream'
    ];

    let total = 0;
    categoryFields.forEach((field) => {
      const valueStr = getHistoricalValue(year, field);
      const value = parseFloat(valueStr) || 0;
      total += value;
    });

    return total;
  };

  // Validate year fields before saving
  const validateYearFields = (): boolean => {
    const start = parseInt(startYear);
    const end = parseInt(endYear);
    const base = parseInt(baselineYear);

    // Check if start year is greater than end year
    if (!isNaN(start) && !isNaN(end) && start > end) {
      setSnackbarMessage("Start year cannot be greater than end year.");
      setShowSnackbar(true);
      return false;
    }

    // Check if baseline year is within start and end year range
    if (!isNaN(base) && !isNaN(start) && !isNaN(end)) {
      if (base < start || base > end) {
        setSnackbarMessage("Baseline year must be within the start year and end year range.");
        setShowSnackbar(true);
        return false;
      }
    }

    return true;
  };

  // Helper function to check if a year is the baseline year
  const isBaselineYear = (year: string): boolean => {
    return year === baselineYear && baselineYear !== "";
  };

  // Helper function to get cell background color based on baseline year
  const getCellBackgroundColor = (year: string, defaultColor: string): string => {
    return isBaselineYear(year) ? '#bbdefb' : defaultColor; // Light blue for baseline year (matches primary color theme)
  };

  // Scope 3 categories
  const scope3Categories = [
    { name: "Category 1: Purchased Goods & Services", tooltip: TitleDefinitionConstants.LocationDetails.BaselineScope3Category1, fieldName: "purchasedGoodsAndServices" },
    { name: "Category 2: Capital Goods", tooltip: TitleDefinitionConstants.LocationDetails.BaselineScope3Category2, fieldName: "capitalGoods" },
    { name: "Category 3: Fuel- And Energy-Related Activities", tooltip: TitleDefinitionConstants.LocationDetails.BaselineScope3Category3, fieldName: "fuelandEnergyRelatedActivities" },
    { name: "Category 4: Upstream Transportation & Distribution", tooltip: TitleDefinitionConstants.LocationDetails.BaselineScope3Category4, fieldName: "upstreamTransportationAndDistribution" },
    { name: "Category 5: Waste Generated in Operations", tooltip: TitleDefinitionConstants.LocationDetails.BaselineScope3Category5, fieldName: "wasteGeneratedinOperations" },
    { name: "Category 6: Business Travel", tooltip: TitleDefinitionConstants.LocationDetails.BaselineScope3Category6, fieldName: "businessTravel" },
    { name: "Category 7: Employee Commuting", tooltip: TitleDefinitionConstants.LocationDetails.BaselineScope3Category7, fieldName: "employeeCommuting" },
    { name: "Category 8: Upstream Leased Assets", tooltip: TitleDefinitionConstants.LocationDetails.BaselineScope3Category8, fieldName: "upstreamLeasedAssets" },
    { name: "Category 9: Downstream Transportation And Distribution", tooltip: TitleDefinitionConstants.LocationDetails.BaselineScope3Category9, fieldName: "downstreamTransportationAndDistribution" },
    { name: "Category 10: Processing of Sold Products", tooltip: TitleDefinitionConstants.LocationDetails.BaselineScope3Category10, fieldName: "processingofSoldProducts" },
    { name: "Category 11: Use of Sold Products", tooltip: TitleDefinitionConstants.LocationDetails.BaselineScope3Category11, fieldName: "useofSoldProducts" },
    { name: "Category 12: End of Life Treatment of Sold Products", tooltip: TitleDefinitionConstants.LocationDetails.BaselineScope3Category12, fieldName: "endofLifeTreatmentofSoldProducts" },
    { name: "Category 13: Downstream Leased Assets", tooltip: TitleDefinitionConstants.LocationDetails.BaselineScope3Category13, fieldName: "downstreamLeasedAssets" },
    { name: "Category 14: Franchises", tooltip: TitleDefinitionConstants.LocationDetails.BaselineScope3Category14, fieldName: "franchises" },
    { name: "Category 15: Investments", tooltip: TitleDefinitionConstants.LocationDetails.BaselineScope3Category15, fieldName: "investments" },
    { name: "Category 16: Other Upstream Scope 3 Emissions", tooltip: TitleDefinitionConstants.LocationDetails.BaselineScope3Category16, fieldName: "otherUpstream" },
    { name: "Category 17: Other Downstream Scope 3 Emissions", tooltip: TitleDefinitionConstants.LocationDetails.BaselineScope3Category17, fieldName: "otherDownstream" },
  ];

  // Build historicalEmissionData from local state for saving
  const buildHistoricalEmissionData = () => {
    const start = parseInt(startYear);
    const end = parseInt(endYear);
    if (isNaN(start) || isNaN(end) || start > end) return [];

    const emissionFields = [
      'scope1GHGEmission', 'scope2GHGEmission', 'scope3GHGEmission',
      'purchasedGoodsAndServices', 'capitalGoods', 'fuelandEnergyRelatedActivities',
      'upstreamTransportationAndDistribution', 'wasteGeneratedinOperations', 'businessTravel',
      'employeeCommuting', 'upstreamLeasedAssets', 'downstreamTransportationAndDistribution',
      'processingofSoldProducts', 'useofSoldProducts', 'endofLifeTreatmentofSoldProducts',
      'downstreamLeasedAssets', 'franchises', 'investments', 'otherUpstream', 'otherDownstream'
    ];

    const historicalData = [];
    for (let year = start; year <= end; year++) {
      const yearStr = year.toString();
      
      // Check if there's an existing record for this year to preserve its ID
      const existingRecord = props.location.historicalEmissionDatas?.find(
        (data) => data.emissionYear === year
      );
      
      const yearData: any = {
        id: existingRecord?.id ?? 0, // Preserve existing ID, use 0 for new records
        emissionYear: year,
        locationID: props.location.locationId,
      };

      emissionFields.forEach((field) => {
        const key = `${yearStr}-${field}`;
        const value = localInputValues[key];
        if (value !== undefined && value !== '') {
          yearData[field] = parseFloat(value) || null;
        } else {
          yearData[field] = null;
        }
      });

      // Calculate scope3GHGEmission as sum of all scope 3 categories
      const scope3CategoryFields = [
        'purchasedGoodsAndServices', 'capitalGoods', 'fuelandEnergyRelatedActivities',
        'upstreamTransportationAndDistribution', 'wasteGeneratedinOperations', 'businessTravel',
        'employeeCommuting', 'upstreamLeasedAssets', 'downstreamTransportationAndDistribution',
        'processingofSoldProducts', 'useofSoldProducts', 'endofLifeTreatmentofSoldProducts',
        'downstreamLeasedAssets', 'franchises', 'investments', 'otherUpstream', 'otherDownstream'
      ];
      const scope3Total = scope3CategoryFields.reduce((sum, field) => {
        return sum + (yearData[field] ?? 0);
      }, 0);
      yearData.scope3GHGEmission = scope3Total > 0 ? scope3Total : null;

      historicalData.push(yearData);
    }

    return historicalData;
  };

  return (
    <form
      onSubmit={handleSubmit(() => {
        if (validateYearFields()) {
          const locationToSave: LocationModel = {
            ...props.location,
            historicalEmissionDatas: buildHistoricalEmissionData(),
          };
          props.onSave(locationToSave);
        }
      })}
    >
      <Box sx={{ m: 1, fontSize: '0.75rem' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange}
            sx={{
              minHeight: '32px',
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.75rem',
                minWidth: 'auto',
                minHeight: '32px',
                py: 0.5,
                px: 2,
              },
              '& .Mui-selected': {
                color: 'primary.main',
              },
              '& .MuiTabs-indicator': {
                backgroundColor: 'primary.main',
              },
            }}
          >
            <Tab label="General Information" />
            <Tab label="Historical Emission Data" />
          </Tabs>
        </Box>

        {/* Tab Panel 1 - General Information */}
        {activeTab === 0 && (
        <Box sx={{ py: 1 }}>
        <Grid container spacing={2}>
          {/* Location Information Section */}
          <Grid item xs={12}>
            <Typography
              variant="body1"
              sx={{ fontWeight: "bold", color: "gray", mb: 0, fontSize: '0.85rem' }}
            >
              Location Information
            </Typography>
          </Grid>
          
          {/* Location Name - Full Width */}
          {props.location && (
            <Grid item xs={6}>
              <Stack direction={"row"} alignItems="center" spacing={1}>
                <Box sx={{ flexGrow: 1 }}>
                  {props.readonly ? (
                    <Box>
                      <Typography variant="caption" sx={{ color: 'gray', fontSize: '0.7rem' }}>Location Name</Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 500 }}>{props.location.locationName ?? '-'}</Typography>
                    </Box>
                  ) : (
                  <TextField
                    required={!props.readonly}
                    {...register("locationName", {
                      maxLength: {
                        value: 50,
                        message: "Location Name is too long",
                      },
                      validate: {
                        locationNameValidate: async (value) => {
                          const isExist = await props.isLocationExist(
                            value ?? ""
                          );
                          return !isExist || "Location name already exists.";
                        },
                      },
                    })}
                    id="locationName"
                    inputProps={{ "data-testid": "locationName" }}
                    label="Location Name"
                    fullWidth
                    size="small"
                    name={"locationName"}
                    value={props.location.locationName}
                    onChange={(e) => {
                      clearErrors("locationName");
                      props.fieldChangeHandler(e);
                    }}
                    error={!!errors.locationName}
                    helperText={errors.locationName?.message}
                    disabled={props.readonly}
                    sx={{ '& .MuiInputBase-root': { height: '32px', fontSize: '0.75rem' }, '& .MuiInputLabel-root': { fontSize: '0.75rem' } }}
                  />
                  )}
                </Box>
                {!props.readonly && (
                <TitleDefinition
                  content="Enter a unique name for this location."
                  placement="top"
                />
                )}
              </Stack>
            </Grid>
          )}
          <Grid item xs={6}>
          </Grid>
          {/* Currency - Left Column */}
          <Grid item xs={6}>
            <Stack direction={"row"} alignItems="center" spacing={1}>
              <Box sx={{ flexGrow: 1 }}>
                {props.readonly ? (
                  <Box>
                    <Typography variant="caption" sx={{ color: 'gray', fontSize: '0.7rem' }}>Currency</Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 500 }}>{props.location.currency ?? '-'}</Typography>
                  </Box>
                ) : (
                <SingleDropDown
                  required={!props.readonly}
                  inputLabel="Currency"
                  onChange={(e) => {
                    props.fieldChangeHandler(e);
                  }}
                  list={Currency.currency}
                  value={props.location.currency}
                  name={"currency"}
                  size="small"
                  disabled={props.readonly}
                  sx={{ '& .MuiInputBase-root': { height: '32px', fontSize: '0.75rem' }, '& .MuiInputLabel-root': { fontSize: '0.75rem' } }}
                />
                )}
              </Box>
              {!props.readonly && (
              <TitleDefinition
                content={TitleDefinitionConstants.LocationDetails.Currency}
                placement="top"
              />
              )}
            </Stack>
          </Grid>

          {/* Number of Employees - Right Column */}
          <Grid item xs={6}>
            <Stack direction={"row"} alignItems="center" spacing={1}>
              <Box sx={{ flexGrow: 1 }}>
                {props.readonly ? (
                  <Box>
                    <Typography variant="caption" sx={{ color: 'gray', fontSize: '0.7rem' }}>Number of Employees</Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 500 }}>{props.location.noOfEmployees ?? '-'}</Typography>
                  </Box>
                ) : (
                <SingleDropDown
                  onChange={props.fieldChangeHandler}
                  inputLabel="Number of Employees"
                  list={props.NoOfEmployeesRangeList}
                  value={props.location.noOfEmployees}
                  required={!props.readonly}
                  name={"noOfEmployees"}
                  size="small"
                  disabled={props.readonly}
                  sx={{ '& .MuiInputBase-root': { height: '32px', fontSize: '0.75rem' }, '& .MuiInputLabel-root': { fontSize: '0.75rem' } }}
                />
                )}
              </Box>
              {!props.readonly && (
              <TitleDefinition
                content={
                  TitleDefinitionConstants.LocationDetails.NumberOfEmployees
                }
                placement="top"
              />
              )}
            </Stack>
          </Grid>
          
          {/* Business Units Section */}
          <Grid item xs={12}>
             <Divider />
            <Typography
              variant="body1"
              sx={{ fontWeight: "bold", color: "gray", mb: 0, mt: 1, fontSize: '0.85rem' }}
            >
              Business Units
            </Typography>
          </Grid>

          {/* Number of Business Units - Full Width */}
          <Grid item xs={6}>
            <Stack direction={"row"} alignItems="center" spacing={1}>
              <Box sx={{ flexGrow: 1 }}>
                {props.readonly ? (
                  <Box>
                    <Typography variant="caption" sx={{ color: 'gray', fontSize: '0.7rem' }}>Number of Business Units</Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 500 }}>{props.location.businessUnits?.length ?? '-'}</Typography>
                  </Box>
                ) : (
                <SingleDropDown
                  onChange={props.AddBusinessUnit}
                  inputLabel="Number of Business Units"
                  list={props.businessUnitList}
                  value={props.location.businessUnits?.length ? props.location.businessUnits.length.toString() : ""}
                  required={!props.readonly}
                  name={"noOfBusinessUnits"}
                  size="small"
                  disabled={props.readonly}
                  sx={{ '& .MuiInputBase-root': { height: '32px', fontSize: '0.75rem' }, '& .MuiInputLabel-root': { fontSize: '0.75rem' } }}
                />
                )}
              </Box>
              {!props.readonly && (
              <TitleDefinition
                content={
                  TitleDefinitionConstants.LocationDetails
                    .NumberOfBusinessUnits
                }
                placement="top"
              />
              )}
            </Stack>
          </Grid>
          <Grid item xs={6}>
          </Grid>
          {/* Business Unit Fields - Two Columns */}
          {props.location?.businessUnits?.map((businessUnit, index) => (
            <Grid 
              item 
              xs={6}
              key={"businessunit-" + businessUnit.id}
            >
              <Stack direction={"row"} alignItems="center" spacing={1}>
                <Box sx={{ flexGrow: 1 }}>
                  {props.readonly ? (
                    <Box>
                      <Typography variant="caption" sx={{ color: 'gray', fontSize: '0.7rem' }}>Unit {index + 1}</Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 500 }}>{businessUnit.name ?? '-'}</Typography>
                    </Box>
                  ) : (
                  <TextField
                    required={!props.readonly}
                    id={"businessUnit" + index}
                    inputProps={{ "data-testid": "businessUnit" + index }}
                    label={`Unit ${index + 1}`}
                    fullWidth
                    size="small"
                    name={"businessUnit" + index}
                    value={businessUnit.name}
                    onChange={(e) => {
                      props.UpdateBusinessUnit(e, index);
                    }}
                    disabled={props.readonly}
                    sx={{ '& .MuiInputBase-root': { height: '32px', fontSize: '0.75rem' }, '& .MuiInputLabel-root': { fontSize: '0.75rem' } }}
                  />
                  )}
                </Box>
                <Box sx={{ width: 24 }}></Box>
              </Stack>
            </Grid>
          ))}
        </Grid>
        </Box>
        )}

        {/* Tab Panel 2 - Historical Emission Data */}
        {activeTab === 1 && (
        <Box sx={{ py: 1 }}>
          {/* <Typography
            variant="body1"
            sx={{ fontWeight: "bold", color: "gray", mb: 2, fontSize: '0.85rem' }}
          >
            Historical Emissions
          </Typography> */}

          {/* Start Year, End Year, and Baseline Year */}
          <Grid container spacing={2} sx={{ mb: 1.5 }}>
            <Grid item xs={4}>
              <SingleDropDown
                inputLabel="Start Year"
                onChange={(e) => {
                  setStartYear(e.target.value);
                  setBaselineYear("");
                  props.fieldChangeHandler({
                    target: { name: "startYear", value: parseInt(e.target.value) }
                  } as any);
                }}
                list={yearOptions}
                value={startYear}
                required={!props.readonly}
                name={"startYear"}
                size="small"
                disabled={props.readonly}
                sx={{ '& .MuiInputBase-root': { height: '32px', fontSize: '0.75rem' }, '& .MuiInputLabel-root': { fontSize: '0.75rem' } }}
              />
            </Grid>
            <Grid item xs={4}>
              <SingleDropDown
                inputLabel="End Year"
                onChange={(e) => {
                  setEndYear(e.target.value);
                  setBaselineYear("");
                  props.fieldChangeHandler({
                    target: { name: "endYear", value: parseInt(e.target.value) }
                  } as any);
                }}
                list={yearOptions}
                value={endYear}
                required={!props.readonly}
                name={"endYear"}
                size="small"
                disabled={props.readonly}
                sx={{ '& .MuiInputBase-root': { height: '32px', fontSize: '0.75rem' }, '& .MuiInputLabel-root': { fontSize: '0.75rem' } }}
              />
            </Grid>
            <Grid item xs={4}>
              <SingleDropDown
                inputLabel="Target Baseline Year"
                onChange={(e) => {
                  setBaselineYear(e.target.value);
                  props.fieldChangeHandler({
                    target: { name: "baseYear", value: parseInt(e.target.value) }
                  } as any);
                }}
                list={baselineYearOptions}
                value={baselineYear}
                required={!props.readonly}
                name={"baseYear"}
                disabled={props.readonly || baselineYearOptions.length === 0}
                size="small"
                sx={{ '& .MuiInputBase-root': { height: '32px', fontSize: '0.75rem' }, '& .MuiInputLabel-root': { fontSize: '0.75rem' } }}
              />
            </Grid>
          </Grid>

          {/* Title */}
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1, mt: 1.5 }}>
            <Typography
              variant="body1"
              sx={{ fontWeight: "bold", color: "gray", fontSize: '0.85rem' }}
            >
              Baseline Scope 1, 2, & 3 GHG Emissions
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "gray", fontSize: "0.7rem", fontStyle: "italic" }}
            >
              Note: all the values are in metric tons CO₂e
            </Typography>
          </Stack>

          {/* Emissions Table */}
          <TableContainer component={Paper} sx={{ maxHeight: 400, overflow: 'auto' }}>
            <Table stickyHeader size="small" sx={{ '& .MuiTableCell-root': { py: 0.25, px: 0.5, fontSize: '0.7rem' } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#9e9e9e', color: 'white', minWidth: 55, py: 0.5, fontSize: '0.7rem' }}>Scope</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#9e9e9e', color: 'white', minWidth: 180, py: 0.5, fontSize: '0.7rem' }}>Category</TableCell>
                  {yearColumns.length > 0 ? (
                    yearColumns.map((year) => (
                      <TableCell key={year} sx={{ 
                        fontWeight: 'bold', 
                        backgroundColor: isBaselineYear(year) ? '#1976d2' : '#9e9e9e', 
                        color: 'white', 
                        minWidth: 80, 
                        py: 0.5,
                        fontSize: '0.7rem'
                      }}>
                        {year}{isBaselineYear(year) && ' (Baseline)'}
                      </TableCell>
                    ))
                  ) : (
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#9e9e9e', color: 'white', minWidth: 80, py: 0.5, fontSize: '0.7rem' }}>
                      [last year]
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Total Baseline Emissions Row */}
                <TableRow>
                  <TableCell sx={{ backgroundColor: '#e3f2fd', fontWeight: 'bold' }}></TableCell>
                  <TableCell sx={{ backgroundColor: '#e3f2fd', fontWeight: 'bold' }}>
                    <Typography variant="body2" sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}>
                      Total Baseline Emissions (tCO2e)
                    </Typography>
                  </TableCell>
                  {yearColumns.length > 0 ? (
                    yearColumns.map((year) => (
                      <TableCell key={`total-${year}`} sx={{ backgroundColor: getCellBackgroundColor(year, '#e3f2fd'), fontWeight: 'bold' }}>
                        <TextField
                          size="small"
                          fullWidth
                          variant="outlined"
                          type="text"
                          disabled
                          value={calculateTotalEmissions(year).toLocaleString("en-US")}
                          sx={{ '& .MuiInputBase-root': { fontSize: '0.7rem', backgroundColor: '#f5f5f5', height: '22px', fontWeight: 'bold' }, '& .MuiInputBase-input': { py: 0.25, px: 0.5 } }}
                        />
                      </TableCell>
                    ))
                  ) : (
                    <TableCell sx={{ backgroundColor: '#e3f2fd' }} />
                  )}
                </TableRow>

                {/* Scope 1 */}
                <TableRow>
                  <TableCell sx={{ backgroundColor: '#f5f5f5' }}>Scope 1</TableCell>
                  <TableCell sx={{ backgroundColor: '#f5f5f5' }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography variant="body2" sx={{ fontSize: '0.7rem' }}>All</Typography>
                      <TitleDefinition
                        content={TitleDefinitionConstants.LocationDetails.BaselineScope1}
                        placement="top"
                      />
                    </Stack>
                  </TableCell>
                  {yearColumns.length > 0 ? (
                    yearColumns.map((year, yearIndex) => (
                      <TableCell key={`scope1-${year}`} sx={{ backgroundColor: getCellBackgroundColor(year, '#f5f5f5') }}>
                        <TextField
                          size="small"
                          fullWidth
                          variant="outlined"
                          type="text"
                          value={getHistoricalValue(year, "scope1GHGEmission")}
                          onChange={handleHistoricalDataChange(year, "scope1GHGEmission")}
                          onBlur={handleHistoricalDataBlur(year, "scope1GHGEmission")}
                          inputProps={{ tabIndex: yearIndex * 19 + 1 }}
                          disabled={props.readonly}
                          sx={{ '& .MuiInputBase-root': { fontSize: '0.7rem', backgroundColor: 'white', height: '22px' }, '& .MuiInputBase-input': { py: 0.25, px: 0.5 } }}
                        />
                      </TableCell>
                    ))
                  ) : (
                    <TableCell sx={{ backgroundColor: '#457b9d', verticalAlign: 'middle' }} rowSpan={21}>
                      <Box sx={{ 
                        color: 'white', 
                        textAlign: 'center', 
                        py: 4,
                        fontWeight: 'bold',
                        fontSize: '0.75rem'
                      }}>
                        Select start year and end year to input historical emissions data.
                      </Box>
                    </TableCell>
                  )}
                </TableRow>

                {/* Scope 2 */}
                <TableRow>
                  <TableCell>Scope 2</TableCell>
                  <TableCell>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography variant="body2" sx={{ fontSize: '0.7rem' }}>All</Typography>
                      <TitleDefinition
                        content={TitleDefinitionConstants.LocationDetails.BaselineScope2}
                        placement="top"
                      />
                    </Stack>
                  </TableCell>
                  {yearColumns.length > 0 && (
                    yearColumns.map((year, yearIndex) => (
                      <TableCell key={`scope2-${year}`} sx={{ backgroundColor: getCellBackgroundColor(year, 'white') }}>
                        <TextField
                          size="small"
                          fullWidth
                          variant="outlined"
                          type="text"
                          value={getHistoricalValue(year, "scope2GHGEmission")}
                          onChange={handleHistoricalDataChange(year, "scope2GHGEmission")}
                          onBlur={handleHistoricalDataBlur(year, "scope2GHGEmission")}
                          inputProps={{ tabIndex: yearIndex * 19 + 2 }}
                          disabled={props.readonly}
                          sx={{ '& .MuiInputBase-root': { fontSize: '0.7rem', backgroundColor: 'white', height: '22px' }, '& .MuiInputBase-input': { py: 0.25, px: 0.5 } }}
                        />
                      </TableCell>
                    ))
                  )}
                </TableRow>

                {/* Scope 3 - ALL */}
                <TableRow>
                  <TableCell sx={{ backgroundColor: '#f5f5f5' }}>Scope 3</TableCell>
                  <TableCell sx={{ backgroundColor: '#f5f5f5' }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography variant="body2" sx={{ fontSize: '0.7rem' }}>All</Typography>
                      <TitleDefinition
                        content={TitleDefinitionConstants.LocationDetails.BaselineScope3}
                        placement="top"
                      />
                    </Stack>
                  </TableCell>
                  {yearColumns.length > 0 && (
                    yearColumns.map((year) => (
                      <TableCell key={`scope3-all-${year}`} sx={{ backgroundColor: getCellBackgroundColor(year, '#f5f5f5') }}>
                        <TextField
                          size="small"
                          fullWidth
                          variant="outlined"
                          type="text"
                          disabled
                          value={calculateScope3Total(year).toLocaleString("en-US")}
                          sx={{ '& .MuiInputBase-root': { fontSize: '0.7rem', backgroundColor: '#e0e0e0', height: '22px' }, '& .MuiInputBase-input': { py: 0.25, px: 0.5 } }}
                        />
                      </TableCell>
                    ))
                  )}
                </TableRow>

                {/* Scope 3 Categories */}
                {scope3Categories.map((category, index) => (
                  <TableRow key={`scope3-cat-${category.fieldName}`}>
                    <TableCell sx={{ backgroundColor: index % 2 === 0 ? 'white' : '#f5f5f5' }}>Scope 3</TableCell>
                    <TableCell sx={{ backgroundColor: index % 2 === 0 ? 'white' : '#f5f5f5' }}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Typography variant="body2" sx={{ fontSize: '0.7rem' }}>{category.name}</Typography>
                        <TitleDefinition
                          content={category.tooltip}
                          placement="top"
                        />
                      </Stack>
                    </TableCell>
                    {yearColumns.length > 0 && (
                      yearColumns.map((year, yearIndex) => (
                        <TableCell key={`scope3-cat-${category.fieldName}-${year}`} sx={{ backgroundColor: getCellBackgroundColor(year, index % 2 === 0 ? 'white' : '#f5f5f5') }}>
                          <TextField
                            size="small"
                            fullWidth
                            variant="outlined"
                            type="text"
                            value={getHistoricalValue(year, category.fieldName as any)}
                            onChange={handleHistoricalDataChange(year, category.fieldName)}
                            onBlur={handleHistoricalDataBlur(year, category.fieldName)}
                            inputProps={{ tabIndex: yearIndex * 19 + index + 3 }}
                            disabled={props.readonly}
                            sx={{ '& .MuiInputBase-root': { fontSize: '0.7rem', backgroundColor: 'white', height: '22px' }, '& .MuiInputBase-input': { py: 0.25, px: 0.5 } }}
                          />
                        </TableCell>
                      ))
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        )}
      </Box>
      
      {/* Floating Save Button - Sticky within popup */}
      {(props.showSaveButton ?? !props.readonly) && (
      <Box sx={{ 
        position: 'sticky', 
        bottom: 0, 
        backgroundColor: 'white',
        py: 1,
        px: 1,
        display: 'flex',
        justifyContent: 'flex-end',
        borderTop: '1px solid #e0e0e0',
        zIndex: 10
      }}>
        <Button type="submit" variant="contained" data-testid="btnSave" size="small" sx={{ fontSize: '0.75rem' }}>
          Save Location
        </Button>
      </Box>
      )}
      
      {/* Validation Error Snackbar - Positioned to work with dialog */}
      <Box sx={{ position: 'relative', zIndex: 9999 }}>
        <SnackBar
          show={showSnackbar}
          message={snackbarMessage}
          severity="error"
          onclose={() => setShowSnackbar(false)}
        />
      </Box>
    </form>
  );
}